import type { DOMPurify } from "dompurify";

export const EMAIL_BODY_CONTENT_TOKEN = "{content}";
export const EMAIL_SUBJECT_TOKEN = "{subject}";
export const DEFAULT_EMAIL_BODY_TEMPLATE = `<!doctype html>
<html lang="zh-CN">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>{subject}</title>
</head>
<body style="margin:0;background-color:#f5f4f9;font-family:Arial,'Microsoft YaHei',sans-serif">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color:#f5f4f9">
    <tr>
      <td align="center" style="padding:32px 16px">
        <table role="presentation" width="600" cellspacing="0" cellpadding="0" style="width:100%;max-width:600px;background-color:#ffffff;border:1px solid #e6e2ef;border-radius:8px">
          <tr>
            <td style="padding:28px 32px 16px;border-top:4px solid #6b3eec;font-size:15px;line-height:26px;color:#374151;overflow-wrap:anywhere">
              {content}
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
export const EMAIL_BODY_TEMPLATE_MAX_LENGTH = 50_000;

export function resolveEmailBodyTemplate(value = DEFAULT_EMAIL_BODY_TEMPLATE) {
  return /<html(?:\s|>)/i.test(value)
    ? value
    : DEFAULT_EMAIL_BODY_TEMPLATE.replace(EMAIL_BODY_CONTENT_TOKEN, () => value);
}

export function validateEmailBodyTemplate(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim()) return "请填写正文模板";
  if (value.length > EMAIL_BODY_TEMPLATE_MAX_LENGTH) return "正文模板不能超过50000字";
  if (value.split(EMAIL_BODY_CONTENT_TOKEN).length !== 2) return "正文模板必须包含且仅包含一个 {content} 正文变量";
  return null;
}

const allowedStyles = new Set([
  "color", "background", "background-color", "font-size", "font-family", "font-weight", "font-style",
  "text-decoration", "text-align", "text-indent", "line-height", "letter-spacing",
  "margin", "margin-top", "margin-right", "margin-bottom", "margin-left",
  "padding", "padding-top", "padding-right", "padding-bottom", "padding-left",
  "border", "border-top", "border-right", "border-bottom", "border-left", "border-color",
  "border-width", "border-style", "border-radius", "border-collapse", "border-spacing",
  "width", "min-width", "max-width", "height", "min-height", "max-height", "vertical-align", "white-space", "display", "box-sizing", "table-layout", "text-transform",
  "overflow-wrap", "word-break", "list-style-type",

  ...["top", "right", "bottom", "left"].flatMap(side => ["width", "style", "color"].map(part => `border-${side}-${part}`)),
  ...["top-left", "top-right", "bottom-left", "bottom-right"].map(corner => `border-${corner}-radius`),
  "text-decoration-line", "text-decoration-style", "text-decoration-color", "text-decoration-thickness",
]);

export function sanitizeEmailBodyTemplate(value: unknown, purifier: DOMPurify): { html: string; error: string | null } {
  const issue = validateEmailBodyTemplate(value);
  if (issue) return { html: "", error: issue };
  const fragment = purifier.sanitize(resolveEmailBodyTemplate(value as string), {
    WHOLE_DOCUMENT: true,
    ALLOWED_TAGS: ["html", "head", "meta", "title", "body", "p", "br", "div", "span", "code", "pre", "strong", "b", "em", "i", "u", "s", "del", "sub", "sup", "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "li", "blockquote", "a", "img", "table", "thead", "tbody", "tfoot", "tr", "td", "th", "hr"],
    ALLOWED_ATTR: ["lang", "dir", "charset", "name", "content", "style", "href", "src", "alt", "title", "width", "height", "align", "valign", "colspan", "rowspan", "cellpadding", "cellspacing", "border", "role"],
    ADD_URI_SAFE_ATTR: ["lang", "dir", "charset", "name", "content", "width", "height", "align", "valign", "colspan", "rowspan", "cellpadding", "cellspacing", "border"],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|#)/i,
    RETURN_DOM: true,
  }) as HTMLElement;
  for (const element of [fragment, ...fragment.querySelectorAll("*")]) {
    for (const attribute of [...element.attributes]) {
      if ([EMAIL_BODY_CONTENT_TOKEN, EMAIL_SUBJECT_TOKEN].some(token => attribute.value.includes(token))) return { html: "", error: "邮件变量只能放在文字中，不能放在 HTML 属性或样式中" };
    }
    for (const attribute of ["href", "src"]) {
      const url = element.getAttribute(attribute);
      if (url && !(attribute === "src" ? /^https?:\/\//i : /^(?:https?:\/\/|mailto:|#)/i).test(url)) element.removeAttribute(attribute);
    }
    const styled = element as HTMLElement;
    if (styled.style) {
      for (const property of Array.from({ length: styled.style.length }, (_, index) => styled.style.item(index))) {
        const css = styled.style.getPropertyValue(property);
        if (!allowedStyles.has(property) || /(?:url|image-set|cross-fade|paint|element|var|attr|expression)\s*\(|@|\\|[<>\u0000-\u001f]/i.test(css)) styled.style.removeProperty(property);
      }
      if (styled.style.cssText) styled.setAttribute("style", styled.style.cssText);
      else styled.removeAttribute("style");
    }
  }
  for (const meta of fragment.querySelectorAll("meta")) {
    if (meta.hasAttribute("charset")) meta.setAttribute("charset", "utf-8");
    else if (meta.getAttribute("name")?.toLowerCase() !== "viewport") meta.remove();
  }
  if (!fragment.querySelector("body")?.textContent?.includes(EMAIL_BODY_CONTENT_TOKEN)) {
    return { html: "", error: "请将 {content} 放在 body 内的正文文字中" };
  }
  const html = `<!doctype html>\n${fragment.outerHTML}`;
  const cleanIssue = validateEmailBodyTemplate(html);
  return { html, error: cleanIssue ? "清理不支持的 HTML 后缺少 {content}，请将它放在正文文字中" : null };
}
